import {useDisclosure} from '@mantine/hooks';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import type {GetEntryManagementResponse} from '../../../../../core/repositories/mypageEntryManagementsRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
  showWarningSnackBar,
} from '../../../../../shared/components';
import {formatDateTimeForClient} from '../../../usecase/formatDateForClient';
import {useProgressStepActions} from '../../../usecase/useProgressStepActions';
import {useProgressStepDeletionActions} from '../../../usecase/useProgressStepDeletionActions';
import {useProgressStepFormUpdater} from '../../../usecase/useProgressStepFormUpdater';
import {DeleteModal} from '../Presenter/DeleteModal';
import {ProgressStepDrawer} from '../Presenter/Progress/ProgressStepDrawer';

export type StepDataType = {
  stepId: number;
  stepOrder: number;
  stepName: string;
  deadline: string | null; // バックエンドAPIスキーマに合わせてnull許容
  result: string | null; // 選考結果（null = 未設定）
  notes: string | null; // バックエンドAPIスキーマに合わせてnull許容
  startDate?: string | null; // オプショナルかつnull許容
  endDate?: string | null; // オプショナルかつnull許容
  place?: string | null; // オプショナルかつnull許容
};

type Props = {
  selectedEntryManagementId: number | null;
  entryDetailData?: GetEntryManagementResponse | null;
  isSp?: boolean;
  onBack?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
};

export type ProgressStepDrawerRef = {
  openAdd: () => void;
  openEdit: (stepData: StepDataType) => void;
  openWithData: (
    data: Partial<StepDataType>,
    options?: {editMode?: boolean},
  ) => void;
  isOpen: () => boolean;
};

export const ProgressStepDrawerContainer = forwardRef<
  ProgressStepDrawerRef,
  Props
>(
  (
    {
      selectedEntryManagementId,
      entryDetailData,
      isSp = false,
      onBack,
      onOpenChange,
    },
    ref,
  ) => {
    // ステップ作成アクション
    const {form, handleSubmit, isLoading, clearMessages} =
      useProgressStepActions({
        // 現在のエントリー完了状態を渡す（ステップ追加時に状態を保持するため）
        hasCompletedEntry: entryDetailData?.hasCompletedEntry ?? false,
      });

    const {updateForm, isLoading: isUpdateLoading} =
      useProgressStepFormUpdater();
    const {executeStepDeletion, isLoading: isDeletionLoading} =
      useProgressStepDeletionActions();

    const [opened, {open, close}] = useDisclosure(false);
    const [
      deleteModalOpened,
      {open: openDeleteModal, close: closeDeleteModal},
    ] = useDisclosure(false);
    const [isEditMode, setIsEditMode] = useState(false);

    // 前回のopened値を保持するref
    const prevOpenedRef = useRef(opened);

    // ドロワーの開閉状態を親コンポーネントに通知
    // 前回値と比較して変更があった場合のみ通知
    useEffect(() => {
      if (prevOpenedRef.current !== opened) {
        prevOpenedRef.current = opened;
        onOpenChange?.(opened);
      }
    }, [opened, onOpenChange]);

    /**
     * エントリー管理が選択されているかを確認
     */
    const validateEntryManagementSelected = (): boolean => {
      if (!selectedEntryManagementId || !entryDetailData) {
        showErrorSnackBar({
          title: '操作エラー',
          message: 'エントリー管理が選択されていません',
        });
        return false;
      }
      return true;
    };

    /**
     * 次のステップ順序を計算
     */
    const calculateNextStepOrder = (): number => {
      if (!entryDetailData?.steps) return 1;
      const maxStepOrder = entryDetailData.steps.reduce(
        (max: number, step) => Math.max(max, step.stepOrder),
        0,
      );
      return maxStepOrder + 1;
    };

    useImperativeHandle(ref, () => ({
      isOpen: () => opened,
      openAdd: () => {
        if (!validateEntryManagementSelected()) return;

        const nextStepOrder = calculateNextStepOrder();
        form.reset({stepOrder: nextStepOrder});
        setIsEditMode(false);
        open();
      },
      openEdit: (stepData: StepDataType) => {
        form.reset({
          stepOrder: stepData.stepOrder,
          stepName: stepData.stepName,
          deadline: stepData.deadline
            ? formatDateTimeForClient(stepData.deadline)
            : undefined,
          notes: stepData.notes ?? undefined,
          result: stepData.result ?? undefined,
          startDate: stepData.startDate
            ? formatDateTimeForClient(stepData.startDate)
            : undefined,
          endDate: stepData.endDate
            ? formatDateTimeForClient(stepData.endDate)
            : undefined,
          place: stepData.place ?? undefined,
          stepId: stepData.stepId,
        });
        setIsEditMode(true);
        open();
      },
      openWithData: (
        data: Partial<StepDataType>,
        options?: {editMode?: boolean},
      ) => {
        if (!validateEntryManagementSelected()) return;

        const nextStepOrder = calculateNextStepOrder();
        form.reset({
          stepOrder: data.stepOrder ?? nextStepOrder,
          stepName: data.stepName ?? '',
          deadline: data.deadline
            ? formatDateTimeForClient(data.deadline)
            : undefined,
          notes: data.notes ?? undefined,
          result: data.result ?? undefined,
          startDate: data.startDate
            ? formatDateTimeForClient(data.startDate)
            : undefined,
          endDate: data.endDate
            ? formatDateTimeForClient(data.endDate)
            : undefined,
          place: data.place ?? undefined,
          stepId: data.stepId,
        });
        setIsEditMode(options?.editMode ?? false);
        open();
      },
    }));

    /**
     * ドロワーを閉じる共通処理
     * @param shouldCallOnBack - onBackコールバックを呼ぶかどうか
     */
    const closeDrawer = (shouldCallOnBack: boolean) => {
      close();
      form.reset();
      setIsEditMode(false);
      clearMessages();
      if (shouldCallOnBack && onBack) {
        onBack();
      }
    };

    const handleClose = () => closeDrawer(!isEditMode);
    const handleCloseWithoutBack = () => closeDrawer(false);
    const handleBack = () => closeDrawer(true);

    /**
     * ステップフォームの送信処理
     */
    const handleSubmitStepForm = async () => {
      if (!entryDetailData) {
        showErrorSnackBar({
          title: '操作エラー',
          message: '選考管理データが取得できません',
        });
        return;
      }

      const result = isEditMode
        ? await updateForm(selectedEntryManagementId!, form.formState.data)
        : await handleSubmit(
            entryDetailData.companyID || undefined,
            entryDetailData.companyName,
          );

      if (result.success) {
        if (result.googleSyncFailed) {
          showWarningSnackBar({
            title: 'Google同期失敗',
            message: isEditMode
              ? '選考予定は更新されましたが、Googleカレンダーへの同期に失敗しました。カレンダー画面の再同期から復旧できます'
              : '選考予定は追加されましたが、Googleカレンダーへの同期に失敗しました。カレンダー画面の再同期から復旧できます',
          });
        } else {
          showSuccessSnackBar({
            title: isEditMode
              ? '選考予定を更新しました'
              : '選考予定を追加しました',
            message: '選考予定を正常に保存しました',
          });
        }
        if (isEditMode) {
          close();
          form.reset();
          clearMessages();
          setIsEditMode(false);
        } else {
          handleCloseWithoutBack();
        }
      } else {
        showErrorSnackBar({
          title: isEditMode ? '選考予定の更新に失敗' : '選考予定の追加に失敗',
          message: '保存に失敗しました。もう一度お試しください',
        });
      }
    };

    /**
     * 削除情報の検証
     */
    const validateDeletionData = (): boolean => {
      if (!selectedEntryManagementId || !form.formState.data.stepId) {
        showErrorSnackBar({
          title: '削除エラー',
          message: '選考予定の情報が不足しています',
        });
        return false;
      }
      return true;
    };

    /**
     * 削除モーダルを開く
     */
    const handleOpenDeleteModal = () => {
      if (!validateDeletionData()) return;
      openDeleteModal();
    };

    /**
     * 削除を実行
     */
    const handleConfirmDelete = () => {
      if (!validateDeletionData()) return;

      executeStepDeletion(
        {
          entryManagementId: selectedEntryManagementId!,
          stepId: form.formState.data.stepId!,
        },
        {
          onSuccess: () => {
            showSuccessSnackBar({
              title: '選考予定を削除しました',
              message: '選考予定を正常に削除しました',
            });
            closeDeleteModal();
            handleCloseWithoutBack();
          },
          onError: () => {
            showErrorSnackBar({
              title: '選考予定の削除に失敗',
              message: '削除に失敗しました。もう一度お試しください',
            });
          },
        },
      );
    };

    return (
      <>
        <ProgressStepDrawer
          opened={opened}
          onClose={handleClose}
          isSp={isSp}
          onBack={!isEditMode ? handleBack : undefined}
          formData={form.formState.data}
          onFormChange={form.updateField}
          onFieldBlur={form.validateField}
          onSubmit={handleSubmitStepForm}
          isLoading={isLoading || isUpdateLoading || isDeletionLoading}
          errors={form.formState.errors}
          editMode={isEditMode}
          onDelete={isEditMode ? handleOpenDeleteModal : undefined}
        />

        <DeleteModal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          onDelete={handleConfirmDelete}
        />
      </>
    );
  },
);

ProgressStepDrawerContainer.displayName = 'ProgressStepDrawerContainer';
